import {
    useState,
    useEffect,
    useCallback,
    useImperativeHandle,
    forwardRef,
} from "react";
import { AppId } from "../../app/appTypes";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAllSuppliers } from "../suppliers/suppliersSlice";
import { loadProducers, Producer } from "./producers";
import { unwrapResult } from "@reduxjs/toolkit";
import { Localized } from "@fluent/react";
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Tab,
    Box,
    Typography,
    Skeleton,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { If } from "../../app/If";

type FormTabs = "external" | "internal";

export interface CompanyBrowserFormRef {
    getSelectedCompanies: () => Set<AppId>;
    getSelectedSuppliers: () => Set<AppId>;
}

interface CompanyBrowserFormProps {
    filter: Set<AppId>;
    processing: boolean;
}

export const CompanyBrowserForm = forwardRef<CompanyBrowserFormRef, CompanyBrowserFormProps>(
    ({ filter }, ref) => {
        const dispatch = useAppDispatch();
        const [tab, setTab] = useState<FormTabs>("external");
        const [allCompanies, setAllCompanies] = useState<Producer[] | null>(null);
        const [selection, setSelection] = useState<Set<AppId>>(new Set());
        const [selectionSupplier, setSupplierSelection] = useState<Set<AppId>>(new Set());

        useEffect(() => {
            dispatch(loadProducers())
                .then(unwrapResult)
                .then(setAllCompanies)
                .catch(console.error);
        }, [dispatch]);

        const allSuppliers = useAppSelector((state) =>
            selectAllSuppliers(state).filter((x) => !filter.has(x.id))
        ).sort((a, b) => a.shortName.localeCompare(b.shortName));

        const handleCheckboxChange = (id: AppId, checked: boolean, isSupplier: boolean) => {
            const update = (set: Set<AppId>, updateFn: (newSet: Set<AppId>) => void) => {
                const newSet = new Set(set);
                if (checked) newSet.add(id);
                else newSet.delete(id);
                updateFn(newSet);
            };

            if (isSupplier) {
                update(selectionSupplier, setSupplierSelection);
            } else {
                update(selection, setSelection);
            }
        };

        const handleTabChange = useCallback((_: unknown, newTab: FormTabs) => {
            setTab(newTab);
        }, []);

        useImperativeHandle(ref, () => ({
            getSelectedCompanies: () => selection,
            getSelectedSuppliers: () => selectionSupplier,
        }));

        return (
            <Paper sx={{ mt: 2, p: 2 }}>
                <TableContainer sx={{ maxHeight: "calc(100vh - 500px)" }}>
                    <TabContext value={tab}>
                        <TabList onChange={handleTabChange}>
                            <Tab
                                value="external" 
                                label={<Localized id="supplier-external">External</Localized>}
                            />
                            <Tab
                                value="internal"
                                label={<Localized id="supplier-internal">Internal</Localized>}
                            />
                        </TabList>

                        <TabPanel value="internal">
                            <If condition={allCompanies === null}>
                                <Skeleton />
                            </If>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell>
                                            <Localized id="companies-short-name">Nazwa skrócona</Localized>
                                        </TableCell>
                                        <TableCell>
                                            <Localized id="companies-name">Nazwa</Localized>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {allCompanies?.map(({ id, shortName, name }) => (
                                        <TableRow key={id}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selection.has(id)}
                                                    onChange={(_, checked) =>
                                                        handleCheckboxChange(id, checked, false)
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>{shortName}</TableCell>
                                            <TableCell>{name}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TabPanel>

                        <TabPanel value="external">
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell>
                                            <Localized id="companies-short-name">Nazwa skrócona</Localized>
                                        </TableCell>
                                        <TableCell>
                                            <Localized id="companies-name">Nazwa</Localized>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {allSuppliers.map(({ id, shortName, name }) => (
                                        <TableRow key={id}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectionSupplier.has(id)}
                                                    onChange={(_, checked) =>
                                                        handleCheckboxChange(id, checked, true)
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>{shortName}</TableCell>
                                            <TableCell>{name}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TabPanel>
                    </TabContext>
                </TableContainer>
                
            </Paper>
        );
    }
);
